import { trackPageRequest } from './PageLoadingService';
import { Auth, API } from 'aws-amplify';

let pendingSession = null;
let validatedToken = null;
let validationInFlight = null;

// A Cognito session is not a complete Blender account. Revalidate on token
// changes (including restored sessions and account switching).
async function assertAccountExists(session) {
    const token = session.getIdToken().getJwtToken();
    if (validatedToken === token) return;
    if (validationInFlight?.token === token) return validationInFlight.promise;

    const username = String(session.getIdToken().payload['cognito:username'] ||
        session.getIdToken().payload.username || '').trim().toLowerCase();
    if (!username) throw new Error('Account username unavailable. Sign in again.');

    const promise = (async () => {
        const response = await trackPageRequest(API.put('api1169ba1e', '', {
            headers: { Authorization: `Bearer ${token}` },
            body: { category: 'db', tableName: 'users', primaryKey: username, requestOperation: 'get' }
        }));
        if (response?.statusCode && Number(response.statusCode) >= 400) {
            throw new Error('Unable to verify your Blender account. Please try again.');
        }
        const body = response?.body != null
            ? (typeof response.body === 'string' ? JSON.parse(response.body) : response.body)
            : response;
        const item = body?.Item;
        if (!item || !/^0x[0-9a-fA-F]{40}$/.test(String(item.publicKey || '')) ||
            (item.username && String(item.username).toLowerCase() !== username)) {
            validatedToken = null;
            try { await Auth.signOut(); } catch (e) { /* Session may already be gone. */ }
            const error = new Error('Account Not Found. Please complete registration or contact support.');
            error.code = 'ACCOUNT_NOT_FOUND';
            throw error;
        }
        validatedToken = token;
    })();
    validationInFlight = { token, promise };
    try { await promise; } finally {
        if (validationInFlight?.token === token) validationInFlight = null;
    }
}


async function getAuthenticatedSession() {

    if (!pendingSession) {

        pendingSession = trackPageRequest(Auth.currentSession()).finally(() => {
            pendingSession = null;
        });

    }

    try {

        const session = await pendingSession;

        if (!session.isValid()) {
            throw new Error('Session expired');
        }

        return session;

    } catch (error) {

        const sessionError = new Error('Your session expired. Please sign in again.');
        sessionError.code = 'SESSION_EXPIRED';

        throw sessionError;

    }

}

async function authenticatedPut(apiName, path, requestOptions = {}) {

    const session = await getAuthenticatedSession();
    await assertAccountExists(session);

    return trackPageRequest(API.put(apiName, path, {
        ...requestOptions,
        headers: {
            ...requestOptions.headers,
            Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        },
    }));

}

export { authenticatedPut };
